import chroma from 'chroma-js';
import { createGlobalStyle } from 'styled-components';

import {
    breakpoints,
    color,
    radius,
    semanticColor,
    shadow,
    size,
    type
} from './tokens';

export const GlobalStyle = createGlobalStyle<{ alternativeBackground?: boolean }>`
    * {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      font-stretch: normal;
      font-style: normal;
      text-rendering: optimizeLegibility;
    }

    body {
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-tap-highlight-color: transparent;
        background: ${ ({ theme, alternativeBackground }) => (alternativeBackground ? theme.color.white : theme.semanticColor.pageBackground) };
        font-family: ${ type.regularFont }, sans-serif;
        font-size: 14px;
        height: 100%;
        line-height: 1.5;
        margin: 0;
        padding: 0;
        width: 100%;
        color: ${ ({ theme }) => theme.semanticColor.textPrimary };
    }

    button {
        color: ${ ({ theme }) => theme.semanticColor.textPrimary };
    }

    img {
        width: 100%;
    }

    a {
        text-decoration:none
    }
    
    p {
        margin: 0;
        padding: 0;
    }

    html,
    body,
    #root {
        min-height: 100%;
        width: 100%;
    }

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }
`;

export const theme = {
    // new theme structure
    breakpoints,
    color,
    radius,
    semanticColor,
    shadow,
    size,
    type,
    // old theme
    borderColor: '#e7edef',
    borderColorFocused: '#20BCAB',
    borderRadius: '6px',
    borderRadius4: '4px',
    borderRadius30: '30px',
    boxShadowLarge: '0 1px 8px 0 rgba(0, 69, 85, 0.05), 0 3px 3px -2px rgba(0, 69, 85, 0.03), 0 3px 4px 0 rgba(0, 69, 85, 0.03);',
    boxShadowThin: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    desktopBreakpoint: '1024px',
    disabledBackground: 'rgba(61, 180, 213, 0.1)',
    disabledColor: '#C7D2D9',
    fontFamily: 'Roboto, sans-serif',
    fontSize: {
        small: '10px',
        regular: '12px',
        medium: '14px'
    },
    fontWeightBold: '700',
    fontWeightMedium: '500',
    fontWeightRegular: '400',
    fontWeightLight: '300',
    greyBackground: '#A4AFB7',
    greyColor: '#7198a2',
    greyPalette: {
        black: '#004555',
        grey: '#7198a2',
        grey3: '#adc3c7',
        grey4: '#ccdadd',
        grey5: '#e7edef',
        grey6: '#f3f6f7',
        grey7: '#F6F8F9',
        grey8: '#f9f9f9',
        background: '#e3ecef'
    },
    greyLight: '#F9F9F9',
    inverseBackground: '#104452',
    inverseBackgroundTransparent: 'rgba(16, 68, 82, 0.9)',
    inverseBorderColor: '#FFFFFF',
    inverseColor: '#FFFFFF',
    inverseColorTransparent: 'rgba(255, 255, 255, 0.3)',
    lightGreyBackground: '#F6F8F9',
    buttonsBreakpoint: '440px',
    primaryBackground: '#FFFFFF',
    primaryBackgroundTransparent: 'rgba(255,255,255,0.1)',
    primaryColor: '#104452',
    primaryColorPalette: {
        main: color.grey.grey_90,
        transparentLight: chroma(color.grey.grey_100).alpha(0.3).css()
    },
    redColor: '#F26565',
    redColorPalette: {
        main: '#da4c49',
        light1: '#ef5350',
        light2: '#ff8a80'
    },
    secondaryBackground: '#20BCAB',
    secondaryColor: '#20BCAB',
    secondaryBackgroundTransparent: 'rgba(32, 188, 171, 0.1)',
    secondaryColorPalette: {
        main: '#40B4A8',
        light1: '#47C7BB',
        light2: '#71d3c9',
        light3: '#b3e7e2',
        light4: '#cef0ec',
        light5: '#e7f7f6',
        transparent: 'rgba(71, 199, 187, 0.1)'
    },
    statusBackgrounds: {
        New: '#4fc3f7',
        Updated: '#fb8c00',
        Viewed: 'transparent'
    },
    shiftStatus: {
        assigned: '#66BB6A',
        expired: '#FF8A80',
        lost: '#EF5350',
        new: '#81d4fa',
        sent: '#FB8C00',
        accepted: '#FBC02D',
        submitted: '#47C7BB',
        recman: '#8BC34A'
    },
    statusOfCompletionBackgrounds: {
        Accepted: '#fbc02d',
        Assigned: '#66bb6a',
        New: '#4fc3f7',
        Progress: '#fb8c00',
        Submitted: '#47c7bb'
    },
    successColor: '#66bb6a',
    tertiaryBackground: '#E5ECEF',
    tabletBreakpoint: '768px'
} as const;

export type Theme = typeof theme;
